import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input } from '@/components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { sourcesApi } from '@/lib/api';
import type { Source, SourceType } from '@/types/api';

const SOURCE_TYPES: SourceType[] = [
  'rss_feed',
  'single_url',
  'multi_url',
  'manual_url',
  'manual',
  'browser_ext',
];

const TIERS = ['tier_1', 'tier_2', 'tier_3'];

interface AddSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog operates in "edit" mode against this source. */
  editing?: Source | null;
  onSuccess?: (mode: 'add' | 'edit') => void;
}

/**
 * Dual-purpose dialog: registers a new source, or edits an existing one
 * when `editing` is passed. Same field set in both modes; the submit
 * button label + endpoint differ.
 */
export function AddSourceDialog({
  open,
  onOpenChange,
  editing = null,
  onSuccess,
}: AddSourceDialogProps) {
  const { t } = useTranslation('studio');
  const qc = useQueryClient();
  const mode: 'add' | 'edit' = editing ? 'edit' : 'add';

  const [url, setUrl] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('rss_feed');
  const [tier, setTier] = useState('tier_1');
  const [tags, setTags] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Sync form state with the row being edited every time it changes.
  useEffect(() => {
    if (editing) {
      setUrl(editing.source_url);
      setSourceType(editing.source_type);
      setTier(editing.retrieval_tier);
      setTags(editing.topic_tags.join(', '));
    } else {
      setUrl('');
      setSourceType('rss_feed');
      setTier('tier_1');
      setTags('');
    }
    setError(null);
  }, [editing, open]);

  const submit = useMutation({
    mutationFn: (payload: {
      source_url: string;
      source_type: SourceType;
      retrieval_tier: string;
      topic_tags: string[];
    }) => {
      if (editing) return sourcesApi.patch(editing.source_id, payload);
      return sourcesApi.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio', 'sources'] });
      onOpenChange(false);
      onSuccess?.(mode);
    },
    onError: (e: Error) => setError(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!isValidUrl(trimmed)) {
      setError('Invalid URL');
      return;
    }
    submit.mutate({
      source_url: trimmed,
      source_type: sourceType,
      retrieval_tier: tier,
      topic_tags: tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? t('sources.edit.title') : t('sources.add.title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-small">
            <span className="text-slate-700">{t('sources.add.url')}</span>
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('sources.add.urlPlaceholder')}
              required
              autoFocus
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-small">
              <span className="text-slate-700">{t('sources.add.type')}</span>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as SourceType)}
                className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-body text-slate-900 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {SOURCE_TYPES.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-small">
              <span className="text-slate-700">{t('sources.add.tier')}</span>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-body text-slate-900 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {TIERS.map((tl) => (
                  <option key={tl} value={tl}>
                    {tl}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-small text-slate-500">{t('sources.add.tierHint')}</p>

          <label className="block text-small">
            <span className="text-slate-700">{t('sources.add.tags')}</span>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="hydrogen, offshore_wind, eu_policy"
            />
            <span className="mt-0.5 block text-small text-slate-500">
              {t('sources.add.tagsHint')}
            </span>
          </label>

          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submit.isPending}
            >
              {t('sources.add.cancel')}
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {mode === 'edit' ? t('sources.edit.submit') : t('sources.add.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function isValidUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
